import { ContractTemplateOperatorState } from 'dg-web-shared/model/ContractTemplateOperatorState.tsx';
import { JSX } from 'react';
import {
    Box,
    Button,
    Table,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
} from '@mui/material';
import { Localized } from 'dg-web-shared/lib/Localization.tsx';
import {
    Add,
    AdminPanelSettings,
    CheckCircleOutlined,
    Person,
    RemoveCircleOutlined,
    VerifiedUser,
    Work,
    WorkOutlined,
} from '@mui/icons-material';
import { OperatorLoginRole } from '../../common/state/OperatorLoginData.ts';
import { useServerFetch } from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import { Outlet, useNavigate, useOutletContext } from 'react-router-dom';
import { useOperatorContext } from '../../app/components/BaseLayoutAndData.tsx';
import { OperatorAsyncLoadedSection } from '../../app/components/OperatorAsyncLoadedSection.tsx';
import {
    MainContainer,
    MainContentBox,
} from '../../shared-mui-components/MainContent.tsx';
import Alert from '@mui/material/Alert';
import { HeaderWithButtons } from '../../shared-mui-components/HeaderWithButtons.tsx';
import { SeparationBar } from '../../layout/components/SeparationBar.tsx';
import { EmDash } from 'dg-web-shared/lib/Punctuation.ts';
import {
    ConfirmationDialog,
    ConfirmationDialogVersions,
} from './ConfirmationDialog.tsx';
import { EmailSuppressionTag } from '../../shared-mui-components/EmailSuppressionTag.tsx';
import { EmailSuppression } from '../../common/models/EmailSuppression.ts';

export type ProductName = {
    [key in 'de' | 'fr' | 'it' | 'en']: string;
};

export type ProductType = {
    contractTemplateId: number;
    productTemplateVersionId: string;
    productName: ProductName;
    operatorState: ContractTemplateOperatorState;
};

interface OperatorLoginListItem {
    organizationLoginId: string;
    role: OperatorLoginRole;
    lastname: string;
    firstname: string;
    description: string | null;
    email: string | null;
    otpEnabled: boolean;
    activityStatus: OperatorLoginAcitvityStatus;
    emailSuppression: EmailSuppression | null;
}

enum OperatorLoginAcitvityStatus {
    ACTIVE = 'ACTIVE',
    ACTIVE_ON_OTHER_OPERATOR = 'ACTIVE_ON_OTHER_OPERATOR',
    INACTIV = 'INACTIV',
}

export function OperatorLogins() {
    const navigate = useNavigate();
    const {
        operatorData: { operatorExternalId },
        currentLogin,
    } = useOperatorContext();

    if (!currentLogin.permissions.userPermissionEdit) {
        return (
            <ConfirmationDialog
                open
                title={
                    <Localized
                        de="Funktionen verschoben"
                        fr="Fonctions déplacées"
                        it="Funzioni spostate"
                        en="Moved functions"
                    />
                }
                confirmLabel={<Localized de="OK" fr="OK" it="OK" en="OK" />}
                onConfirm={() => {
                    navigate(`/${operatorExternalId}/profile`);
                }}
                dialogVersion={ConfirmationDialogVersions.PRIMARY}
            >
                <p>
                    <Localized
                        de="Sie können Ihr Passwort/2FA im Benutzermenü oben rechts ändern."
                        fr="Vous pouvez modifier votre mot de passe/2FA dans le menu utilisateur, en haut à droite."
                        it="Può modificare la propria password/2FA nel menu utente, in alto a destra."
                        en="You can change your password/2FA in the user menu at the top right."
                    />
                </p>
            </ConfirmationDialog>
        );
    }

    return <OperatorLoginsList />;
}

function OperatorLoginsList() {
    const [operatorLoginListState, refetchOperatorLoginList] = useServerFetch<
        OperatorLoginListItem[],
        object
    >(
        () => ({
            url: `/ui-api/operator-account/operator-login-list`,
        }),
        {},
    );
    const navigate = useNavigate();

    return (
        <OperatorAsyncLoadedSection
            requestState={operatorLoginListState}
            render={logins => (
                <MainContainer>
                    <Alert severity="info" sx={{ mx: 0, mt: 0 }}>
                        <Localized
                            de="Ihre Profileinstellungen finden Sie nun im Benutzermenü oben rechts."
                            fr="Vous trouverez désormais vos paramètres de profil dans le menu utilisateur en haut à droite."
                            it="Le sue impostazioni di profilo sono ora disponibili nel menu utente in alto a destra."
                            en="You can now find your profile settings in the user menu at the top right."
                        />
                    </Alert>
                    <MainContentBox>
                        <HeaderWithButtons
                            title={
                                <Localized
                                    de="Zugriffsrechte"
                                    fr="Droits d'accès"
                                    it="Diritti d'accesso"
                                    en="Access Rights"
                                />
                            }
                            icon={AdminPanelSettings}
                            headerButtons={
                                <Button
                                    variant="contained"
                                    color={'primary'}
                                    startIcon={<Add />}
                                    onClick={() =>
                                        navigate('operator-login-create')
                                    }
                                >
                                    <Localized
                                        de="Neuer Benutzer"
                                        fr="Nouveau utilisateur"
                                        it="Nuovo utente"
                                        en="New user"
                                    />
                                </Button>
                            }
                        />
                        <SeparationBar />
                        <OperatorLoginsListTable logins={logins} />
                        <OperatorLoginListOutlet
                            context={{ refetchOperatorLoginList }}
                        />
                    </MainContentBox>
                </MainContainer>
            )}
        />
    );
}

function OperatorLoginsListTable({
    logins,
}: {
    logins: OperatorLoginListItem[];
}) {
    return (
        <TableContainer>
            <Table stickyHeader>
                <TableHead>
                    <TableRow>
                        <TableCell>
                            <Localized
                                de="Rolle"
                                fr="Rôle"
                                it="Ruolo"
                                en="Role"
                            />
                        </TableCell>
                        <TableCell>
                            <Localized
                                de="Nachname"
                                fr="Nom"
                                it="Cognome"
                                en="Lastname"
                            />
                        </TableCell>
                        <TableCell>
                            <Localized
                                de="Vorname"
                                fr="Prénom"
                                it="Nome"
                                en="Firstname"
                            />
                        </TableCell>
                        <TableCell>
                            <Localized
                                de="Beschreibung"
                                fr="Description"
                                it="Descrizione"
                                en="Description"
                            />
                        </TableCell>
                        <TableCell>
                            <Localized
                                de="E-Mail"
                                fr="E-mail"
                                it="E-mail"
                                en="E-mail"
                            />
                        </TableCell>
                        <TableCell>
                            <Localized
                                de="Zwei-Faktor-Authentifizierung"
                                fr="Authentification à deux facteurs"
                                it="Autenticazione a due fattori"
                                en="Two-factor authentication"
                            />
                        </TableCell>
                        <TableCell>
                            <Localized
                                de="Status"
                                fr="État"
                                it="Stato"
                                en="State"
                            />
                        </TableCell>
                    </TableRow>
                </TableHead>
                {logins.map(login => (
                    <OperatorLoginsListRow
                        key={login.organizationLoginId}
                        login={login}
                    />
                ))}
            </Table>
        </TableContainer>
    );
}

function OperatorLoginsListRow({ login }: { login: OperatorLoginListItem }) {
    const navigate = useNavigate();
    return (
        <TableRow
            hover
            sx={{
                cursor: 'pointer',
                '& .MuiTableCell-root': {
                    color:
                        login.activityStatus ===
                        OperatorLoginAcitvityStatus.ACTIVE
                            ? undefined
                            : 'text.disabled',
                },
            }}
            onClick={() =>
                navigate(`operator-login/${login.organizationLoginId}/general`)
            }
        >
            <TableCell>
                {login.activityStatus ===
                    OperatorLoginAcitvityStatus.ACTIVE && (
                    <OperatorLoginRoleIcon role={login.role} />
                )}
            </TableCell>
            <TableCell>{login.lastname}</TableCell>
            <TableCell>{login.firstname}</TableCell>
            <TableCell>{login.description ?? EmDash}</TableCell>
            <TableCell>
                {login.email ?? EmDash}
                <EmailSuppressionTag suppression={login.emailSuppression} />
            </TableCell>
            <TableCell>
                {login.otpEnabled && (
                    <Box sx={{ display: 'flex', flexShrink: 1 }}>
                        <TwoFactorAuthentificationIcon />
                    </Box>
                )}
            </TableCell>
            <TableCell>
                <OperatorLoginActiveIcon
                    activityStatus={login.activityStatus}
                />
            </TableCell>
        </TableRow>
    );
}

function OperatorLoginRoleIcon({
    role,
}: {
    role: OperatorLoginRole;
}): JSX.Element {
    switch (role) {
        case OperatorLoginRole.ADMIN:
            return (
                <Tooltip
                    title={
                        <Localized
                            de="Administrator"
                            fr="administrateur"
                            it="amministratore"
                            en="administrator"
                        />
                    }
                    placement="bottom"
                >
                    <Work />
                </Tooltip>
            );
        case OperatorLoginRole.SUPER_USER:
            return (
                <Tooltip
                    title={
                        <Localized
                            de="Superuser"
                            fr="superutilisateur"
                            it="superuser"
                            en="superuser"
                        />
                    }
                    placement="bottom"
                >
                    <WorkOutlined />
                </Tooltip>
            );
        case OperatorLoginRole.USER:
            return (
                <Tooltip
                    title={
                        <Localized
                            de="Benutzer"
                            fr="utilisateur"
                            it="utente"
                            en="user"
                        />
                    }
                    placement="bottom"
                >
                    <Person />
                </Tooltip>
            );
    }
}

export function TwoFactorAuthentificationIcon() {
    return (
        <Tooltip
            title={
                <Localized
                    de="2-Faktor-Authentifizierung (2FA) aktiv"
                    fr="Authentification à deux facteurs (2FA) active"
                    it="Autenticazione 2 (2FA) fattori attiva"
                    en="2-factor authentication (2FA) enabled"
                />
            }
            placement="bottom"
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'flex-end',
                }}
            >
                <VerifiedUser />
            </Box>
        </Tooltip>
    );
}

function OperatorLoginActiveIcon({
    activityStatus,
}: {
    activityStatus: OperatorLoginAcitvityStatus;
}): JSX.Element {
    switch (activityStatus) {
        case OperatorLoginAcitvityStatus.ACTIVE:
            return (
                <Tooltip
                    title={
                        <Localized
                            de="aktiv"
                            fr="actif"
                            it="attivo"
                            en="active"
                        />
                    }
                    placement="bottom"
                >
                    <CheckCircleOutlined color="success" />
                </Tooltip>
            );
        case OperatorLoginAcitvityStatus.ACTIVE_ON_OTHER_OPERATOR:
            return (
                <Tooltip
                    title={
                        <Localized
                            de="aktiv (anderes Konto)"
                            fr="actif (autre compte)"
                            it="attivo (altro account)"
                            en="active (other account)"
                        />
                    }
                    placement="bottom"
                >
                    <CheckCircleOutlined color="disabled" />
                </Tooltip>
            );
        case OperatorLoginAcitvityStatus.INACTIV:
            return (
                <Tooltip
                    title={
                        <Localized
                            de="inaktiv"
                            fr="inactif"
                            it="inattivo"
                            en="inactive"
                        />
                    }
                    placement="bottom"
                >
                    <RemoveCircleOutlined color="disabled" />
                </Tooltip>
            );
    }
}

export function useOperatorLoginListOutlet() {
    return useOutletContext<OperatorLoginListOutletContext>();
}

function OperatorLoginListOutlet({
    context,
}: {
    context: OperatorLoginListOutletContext;
}) {
    return <Outlet context={context} />;
}

interface OperatorLoginListOutletContext {
    refetchOperatorLoginList: () => void;
}
